import { computed, Injectable, signal } from '@angular/core';
import { ObjectId } from 'bson';
import { Conversation, ChatMessage, WsMessageCreatePayload, WsDeliveredPayload, WsMessageAck } from '../data-models/chat.modals';
import { environment } from '../../environments/environments';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, map, take } from 'rxjs';
import { StorageService } from './storage.service';
import { DELEGATE_ID, USER_ID } from '../data-models/user-data';
const url = `${environment.chatUrl}`;

/** A user currently typing in a conversation. `name` is optional — the server may or
 *  may not include it; consumers fall back to their own participant map. */
export interface TypingUser {
  id: string;
  name?: string;
}
@Injectable({
  providedIn: 'root'
})
export class ChatStateService {
// Basic state
  conversations = signal<Conversation[]>([]);
  activeConversationId = signal<string | null>(null);
  messagesByConv = signal<Map<string, ChatMessage[]>>(new Map());
  // conversationId -> (userId -> name). Stores the typer's name (when the server sends
  // one) so consumers don't have to resolve it from a participant map.
  private typingUsers = new Map<string, Map<string, string | undefined>>();
  // Per (conversationId, userId) auto-remove timers, so a fresh typing event refreshes
  // the timeout instead of racing a stale one.
  private typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  typingUsersChanged = new BehaviorSubject<{ [conversationId: string]: TypingUser[] }>({});

  // Optimistic-UI seq base: keeps unconfirmed (client) messages sorted *below* every
  // server message (whose seq are small ints) while preserving send order via a counter.
  private static readonly OPTIMISTIC_SEQ_BASE = 1e15;
  private optimisticSeqCounter = 0;

  // Derived signal for active conversation
  messagesForActive = computed(() => {
    const id = this.activeConversationId();
    if (!id) return [];
    return [...(this.messagesByConv().get(id) ?? [])];
  });

  constructor(
    private http: HttpClient,
    private storage: StorageService,
  ) { }

  /** IDs that represent "me" — used to ignore typing events the server echoes back. */
  private selfIds(): string[] {
    const uid = this.storage.getJsonValue(USER_ID);
    const did = this.storage.getJsonValue(DELEGATE_ID);
    return [uid, did].filter(Boolean);
  }

  upsertConversation(c: Conversation) {
    this.conversations.update(list => {
      const idx = list.findIndex(x => x.id === c.id);
      if (idx >= 0) {
        const copy = [...list];
        copy[idx] = { ...copy[idx], ...c };
        return copy;
      }
      return [c, ...list];
    });
  }

  setConversations(cs: Conversation[]) {
    this.conversations.set(cs);
  }

  setActiveConversation(id: string | null) {
    this.activeConversationId.set(id);
  }

  // /** Append message to conversation */
  addMessageFromWs(payload: any) {
    const convId = payload.message.conversation_id; // Use string, not ObjectId
    const msg: ChatMessage = {
      id: payload.message._id,
      conversation_id: convId,
      sender_id: payload.message.sender_id,
      type: payload.message.type,
      media: payload.message.media,
      content: payload.message.content,
      edited: payload.message.edited,
      deleted: payload.message.deleted,
      seq: payload.message.seq,
      created_at: payload.message.created_at,
      updated_at: payload.message.updated_at,
      delivered: false,
      reactions: [],
      read_by: [],
      seen_by: [],
      is_editing : false
    };
    // console.log("message:", msg, payload)
    this.messagesByConv.update(map => {
      const next = new Map(map);
      const arr = next.get(convId) ?? [];

      // Insert only if not duplicate
      if (!arr.some(m => m.id === msg.id)) {
        arr.push(msg);
        arr.sort((a, b) => a.seq - b.seq);
      }

      next.set(convId, arr);
      return next;
    });

    // console.log("Updated Messagess for", convId, this.messagesByConv().get(convId));
  }

  // addMessageFromWs(payload: any) {
  //   const convId = payload.message.conversation_id; // Use string, not ObjectId
  //   const msg: ChatMessage = {
  //     id: payload.message._id,
  //     conversation_id: convId,
  //     sender_id: payload.message.sender_id,
  //     type: 'text',
  //     content: payload.message.content,
  //     edited: payload.message.edited,
  //     deleted: payload.message.deleted,
  //     seq: payload.message.seq,
  //     created_at: payload.message.created_at,
  //     updated_at: payload.message.updated_at,
  //     delivered: false,
  //     reactions: payload.message.reactions ?? [],
  //     read_by: payload.message.read_by ?? [],
  //     seen_by: payload.message.seen_by ?? [],
  //     is_editing: false,
  //   };

  //   this.messagesByConv.update(map => {
  //     const next = new Map(map);
  //     const arr = next.get(convId) ?? [];

  //     // Find index of existing message
  //     const idx = arr.findIndex(m => m.id === msg.id);

  //     if (idx === -1) {
  //       // Insert new
  //       arr.push(msg);
  //     } else {
  //       // Update existing (for edits/deletes/reactions)
  //       arr[idx] = { ...arr[idx], ...msg };
  //     }

  //     // Keep messages sorted
  //     arr.sort((a, b) => a.seq - b.seq);

  //     next.set(convId, arr);
  //     return next;
  //   });
  // }

  /** Mark delivered when server notifies */
  markDelivered(payload: WsDeliveredPayload) {
    const msgId = payload.msg_id;
    this.messagesByConv.update(map => {
      const next = new Map(map);
      for (const [convId, arr] of next.entries()) {
        const idx = arr.findIndex(m => m.id === msgId);
        if (idx >= 0) {
          const copy = [...arr];
          copy[idx] = { ...copy[idx], delivered: true };
          next.set(convId, copy);
          break;
        }
      }
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Optimistic UI
  // ---------------------------------------------------------------------------

  /**
   * Insert a client-side message immediately (before the server confirms) so the
   * sender perceives zero latency. `id` is the client `temp_id` until reconciled.
   */
  addOptimisticMessage(convId: string, input: {
    temp_id: string;
    sender_id: string;
    content: string;
    type: any;
    media?: any;
  }): ChatMessage {
    const nowIso = new Date().toISOString();
    const msg: ChatMessage = {
      id: input.temp_id,
      temp_id: input.temp_id,
      conversation_id: convId,
      sender_id: input.sender_id,
      type: input.type,
      content: input.content,
      media: input.media ?? [],
      edited: false,
      deleted: false,
      seq: ChatStateService.OPTIMISTIC_SEQ_BASE + this.optimisticSeqCounter++,
      created_at: nowIso,
      updated_at: nowIso,
      delivered: false,
      status: 'sending',
      reactions: [],
      read_by: [],
      seen_by: [],
      is_editing: false,
    };

    this.messagesByConv.update(map => {
      const next = new Map(map);
      const arr = [...(next.get(convId) ?? [])];
      arr.push(msg);
      arr.sort((a, b) => a.seq - b.seq);
      next.set(convId, arr);
      return next;
    });

    return msg;
  }

  /**
   * Reconcile an optimistic message with the server ack: swap the temp id for the
   * real `message_id`, apply the real seq/created_at, and flip status to 'sent'.
   * Idempotent, and safe regardless of ack/echo arrival order.
   */
  reconcileAck(ack: WsMessageAck) {
    if (!ack?.temp_id) return;
    this.messagesByConv.update(map => {
      const next = new Map(map);
      for (const [convId, arr] of next.entries()) {
        const idx = arr.findIndex(m => m.temp_id === ack.temp_id);
        if (idx < 0) continue;

        // Echo (message_create) may have arrived first and already inserted the
        // canonical server message — in that case drop the optimistic duplicate.
        const serverExists = arr.some(m => m.id === ack.message_id && m.temp_id !== ack.temp_id);
        const copy = [...arr];
        if (serverExists) {
          copy.splice(idx, 1);
        } else {
          copy[idx] = {
            ...copy[idx],
            id: ack.message_id,
            seq: ack.seq,
            created_at: ack.created_at ?? copy[idx].created_at,
            updated_at: ack.created_at ?? copy[idx].updated_at,
            status: 'sent',
          };
          copy.sort((a, b) => a.seq - b.seq);
        }
        next.set(convId, copy);
        break;
      }
      return next;
    });
  }

  /** Mark an optimistic message as failed (send error / timeout). */
  markMessageFailed(tempId: string) {
    this.setOptimisticStatus(tempId, 'failed');
  }

  /** Flip a failed message back to 'sending' for a retry (reuses the temp id). */
  retryOptimisticMessage(tempId: string) {
    this.setOptimisticStatus(tempId, 'sending');
  }

  private setOptimisticStatus(tempId: string, status: 'sending' | 'failed') {
    this.messagesByConv.update(map => {
      const next = new Map(map);
      for (const [convId, arr] of next.entries()) {
        const idx = arr.findIndex(m => m.temp_id === tempId);
        if (idx < 0) continue;
        const copy = [...arr];
        copy[idx] = { ...copy[idx], status };
        next.set(convId, copy);
        break;
      }
      return next;
    });
  }

  /** Remove a message by temp id (used to delete a failed optimistic message). */
  removeMessage(tempId: string) {
    this.messagesByConv.update(map => {
      const next = new Map(map);
      for (const [convId, arr] of next.entries()) {
        const idx = arr.findIndex(m => m.temp_id === tempId);
        if (idx < 0) continue;
        const copy = [...arr];
        copy.splice(idx, 1);
        next.set(convId, copy);
        break;
      }
      return next;
    });
  }

  createConversation(payload: any) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.post(`${url}/conversations`,payload, config);
  }

  createConversationWithGroup(academy_id: string, payload: any) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.post(`${url}/group/conversations/${academy_id}`,payload, config);
  }

  createConversationWithGroupOffical(academy_id: string, payload: any) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.post(`${url}/group/conversations/${academy_id}/offical`,payload, config);
  }

  createConversationDefault(academy_id: string, payload: any) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.post(`${url}/group/conversations/${academy_id}/default`,payload, config);
  }

  patchConversationDefault(academy_id: string) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.get(`${url}/group/conversations/${academy_id}/default`);
  }

  fetchGroupOfficalParticipants(academy_id: string, group_id: any) {
    return this.http.get(`${url}/group/conversations/${academy_id}/${group_id}`);
  }

  patchGroupOfficalParticipants(id: string, payload: any) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.patch(`${url}/group/conversations/${id}/${id}`, payload, config);
  }

  deleteConversation(convid: string){
    return this.http.delete(`${url}/conversations/${convid}`)
  }

  fetchListofConversation() {
    return this.http.get(`${url}/conversations`);
  }

  fetchConversationInfo(convid: string) {
    return this.http.get(`${url}/conversations/${convid}`);
  }

  fetchMessagesOfConversation(convid: string, lastId?: string) {
    let  params: any  = new HttpParams().append('limit', 50)
    if (lastId) {
      params = params.set('lastId', lastId);
    }
    // console.log("Fetchinsg messages with params:", params, convid, lastId)
    return this.http.get(`${url}/messages/${convid}`, { params: params });
  }

  patchConversationName(convid: string, payload: any) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.patch(`${url}/conversations/${convid}`, payload, config);
  }

  removeParticipantFromConversation(convid: string, participantId: string){
    return this.http.delete(`${url}/conversations/${convid}/participants/${participantId}`)
  }

  addParticipantsToConversation(convid: string, payload: any){
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.post(`${url}/conversations/${convid}/participants`, payload, config)
  }

  fetchParticipantsByIds(convid: string, payload: any){
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') }
    return this.http.post(`${url}/messages/${convid}/deleted`, payload, config)
  }

  handleTyping(payload: { conversation_id: string; user_id: string; userName?: string }) {
    const { conversation_id, user_id, userName } = payload;
    if (!conversation_id || !user_id) return;
    // Ignore our own typing echoed back by the server.
    if (this.selfIds().includes(user_id)) return;

    if (!this.typingUsers.has(conversation_id)) {
      this.typingUsers.set(conversation_id, new Map());
    }
    this.typingUsers.get(conversation_id)!.set(user_id, userName);
    this.emitTypingUsers(conversation_id);

    // Refresh the auto-remove timer: clear any pending one for this (conv,user) so a
    // steady stream of typing events keeps the indicator alive instead of a stale
    // timeout cutting it off.
    const key = `${conversation_id}:${user_id}`;
    const existing = this.typingTimers.get(key);
    if (existing) clearTimeout(existing);
    this.typingTimers.set(key, setTimeout(() => this.removeTyping(conversation_id, user_id), 5000));
  }

  handleStopTyping(payload: { conversation_id: string; user_id: string; userName?: string }) {
    this.removeTyping(payload.conversation_id, payload.user_id);
  }

  private removeTyping(conversationId: string, userId: string) {
    const key = `${conversationId}:${userId}`;
    const timer = this.typingTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.typingTimers.delete(key);
    }
    const convMap = this.typingUsers.get(conversationId);
    if (convMap && convMap.delete(userId)) {
      this.emitTypingUsers(conversationId);
    }
  }

  private emitTypingUsers(conversationId: string) {
    const typingObj: { [conversationId: string]: TypingUser[] } = {};
    for (const [cid, users] of this.typingUsers.entries()) {
      typingObj[cid] = Array.from(users.entries()).map(([id, name]) => ({ id, name }));
    }
    this.typingUsersChanged.next(typingObj);
  }
}
